import React, { useState, useEffect } from 'react';
import { ticketsAPI, agenciesAPI, usersAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { formatDate, handleApiError, showToast, showConfirm } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { MessageSquare, Clock, CheckCircle, XCircle, User, Building2 } from 'lucide-react';

const TicketsListPage = () => {
  const { isRTL, t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterAgency, setFilterAgency] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterCategory, filterAgency]);

  useEffect(() => {
    fetchAgencies();
    fetchSuperAdmins();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterAgency !== 'all') params.agency_id = filterAgency;

      const response = await ticketsAPI.getAll(params);
      setTickets(response.data.tickets || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await agenciesAPI.getAll();
      setAgencies(response.data.agencies || []);
    } catch (err) {
      console.error('Error fetching agencies:', err);
    }
  };

  const fetchSuperAdmins = async () => {
    try {
      const response = await usersAPI.getByRole('super_admin');
      setSuperAdmins(response.data.users || []);
    } catch (err) {
      console.error('Error fetching super admins:', err);
    }
  };

  const handleUpdateStatus = async (ticketId, status, assignedTo = null, resolution = null) => {
    try {
      setUpdatingStatus(true);
      await ticketsAPI.updateStatus(ticketId, {
        status,
        assigned_to: assignedTo,
        resolution,
      });
      showToast.success(t('ticketUpdatedSuccess'));
      await fetchData();
      setSelectedTicket(null);
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm(
      t('confirmDeleteTicket'),
      isRTL
    );

    if (!confirmed) return;

    try {
      await ticketsAPI.delete(id);
      showToast.success(t('ticketDeletedSuccess'));
      await fetchData();
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      open: { label: t('statusOpen'), color: 'bg-blue-100 text-blue-800', icon: MessageSquare },
      in_progress: { label: t('statusInProgress'), color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      resolved: { label: t('statusResolved'), color: 'bg-green-100 text-green-800', icon: CheckCircle },
      closed: { label: t('statusClosed'), color: 'bg-gray-100 text-gray-800', icon: XCircle },
    };
    const statusInfo = statusMap[status] || statusMap.open;
    const Icon = statusInfo.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        <Icon className="w-3 h-3" />
        {statusInfo.label}
      </span>
    );
  };

  const getCategoryLabel = (category) => {
    const categoryMap = {
      technical: t('categoryTechnical'),
      billing: t('categoryBilling'),
      feature_request: t('categoryFeatureRequest'),
      bug: t('categoryBug'),
      other: t('categoryOther'),
    };
    return categoryMap[category] || category;
  };

  const getPriorityBadge = (priority) => {
    const priorityMap = {
      low: { label: t('priorityLow'), color: 'bg-gray-100 text-gray-700' },
      medium: { label: t('priorityMedium'), color: 'bg-blue-100 text-blue-700' },
      high: { label: t('priorityHigh'), color: 'bg-orange-100 text-orange-700' },
      urgent: { label: t('priorityUrgent'), color: 'bg-red-100 text-red-700' },
    };
    const priorityInfo = priorityMap[priority] || priorityMap.medium;
    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${priorityInfo.color}`}>
        {priorityInfo.label}
      </span>
    );
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  const statusCounts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-6 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-5 h-5 text-sky-600" />
            <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
              {isRTL ? 'لوحة التذاكر' : 'Tickets Dashboard'}
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
            {t('manageTicketsTitle')}
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            {t('manageTicketsSubtitle')}
          </p>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 border-slate-200">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          {/* Status Filter */}
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: t('all') },
              { value: 'open', label: t('statusOpen') },
              { value: 'in_progress', label: t('statusInProgress') },
              { value: 'resolved', label: t('statusResolved') },
              { value: 'closed', label: t('statusClosed') },
            ].map(status => (
              <button
                key={status.value}
                onClick={() => setFilterStatus(status.value)}
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  filterStatus === status.value
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {status.label} ({statusCounts[status.value] || 0})
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className={`px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white ${
                isRTL ? 'text-right' : 'text-left'
              }`}
            >
              <option value="all">{t('allCategories')}</option>
              <option value="technical">{t('categoryTechnical')}</option>
              <option value="billing">{t('categoryBilling')}</option>
              <option value="feature_request">{t('categoryFeatureRequest')}</option>
              <option value="bug">{t('categoryBug')}</option>
              <option value="other">{t('categoryOther')}</option>
            </select>

            {/* Agency Filter */}
            <select
              value={filterAgency}
              onChange={(e) => setFilterAgency(e.target.value)}
              className={`px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white ${
                isRTL ? 'text-right' : 'text-left'
              }`}
            >
              <option value="all">{t('allAgencies')}</option>
              {agencies.map(agency => (
                <option key={agency.id} value={agency.id}>
                  {agency.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">
              {t('noTickets')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="p-6 hover:shadow-md transition-shadow rounded-2xl border-slate-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="font-mono text-sm text-gray-500">#{ticket.ticket_number}</span>
                    {getStatusBadge(ticket.status)}
                    {getPriorityBadge(ticket.priority)}
                    <span className="text-sm text-gray-600">
                      {getCategoryLabel(ticket.category)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">
                      {ticket.agency_name}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {ticket.subject}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {ticket.description}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                    <span>{formatDate(ticket.created_at)}</span>
                    {ticket.created_by_name && (
                      <>
                        <span>•</span>
                        <span>
                          {t('by')} {ticket.created_by_name}
                        </span>
                      </>
                    )}
                    {ticket.assigned_to_name && (
                      <>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>
                            {t('ticketAssignedTo')}: {ticket.assigned_to_name}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {ticket.resolution && (
                    <div className={`mt-4 p-3 bg-green-50 border border-green-200 rounded-lg ${isRTL ? 'text-right' : 'text-left'}`}>
                      <p className="text-sm font-medium text-green-900 mb-1">
                        {t('ticketResolution')}:
                      </p>
                      <p className="text-sm text-green-800">{ticket.resolution}</p>
                      {ticket.resolved_by_name && (
                        <p className="text-xs text-green-700 mt-1">
                          {t('by')} {ticket.resolved_by_name} - {formatDate(ticket.resolved_at)}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className={`flex flex-col gap-2 ${isRTL ? 'mr-4' : 'ml-4'}`}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setSelectedTicket(selectedTicket?.id === ticket.id ? null : ticket)}
                  >
                    {selectedTicket?.id === ticket.id ? t('cancelButton') : t('manageButton')}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(ticket.id)}
                  >
                    {t('deleteButton')}
                  </Button>
                </div>
              </div>

              {/* Management Panel */}
              {selectedTicket?.id === ticket.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="space-y-4">
                    {/* Assign To */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('assignTo')}
                      </label>
                      <select
                        value={ticket.assigned_to || ''}
                        onChange={(e) => handleUpdateStatus(ticket.id, ticket.status, e.target.value || null)}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 ${isRTL ? 'text-right' : 'text-left'}`}
                        disabled={updatingStatus}
                      >
                        <option value="">{t('unassigned')}</option>
                        {superAdmins.map(admin => (
                          <option key={admin.id} value={admin.id}>
                            {admin.name} ({admin.email})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Update Status */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('changeStatus')}
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {['open', 'in_progress', 'resolved', 'closed'].map(status => (
                          <Button
                            key={status}
                            variant={ticket.status === status ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => handleUpdateStatus(ticket.id, status, ticket.assigned_to)}
                            disabled={updatingStatus || ticket.status === status}
                          >
                            {status === 'open' && t('statusOpen')}
                            {status === 'in_progress' && t('statusInProgress')}
                            {status === 'resolved' && t('statusResolved')}
                            {status === 'closed' && t('statusClosed')}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Resolution (only if resolving) */}
                    {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                      <div>
                        <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('addResolution')}
                        </label>
                        <textarea
                          id={`resolution-${ticket.id}`}
                          rows={3}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 ${isRTL ? 'text-right' : 'text-left'}`}
                          placeholder={t('resolutionPlaceholder')}
                        />
                        <Button
                          variant="primary"
                          size="sm"
                          className={`mt-2 ${isRTL ? 'float-right' : 'float-left'}`}
                          onClick={() => {
                            const resolution = document.getElementById(`resolution-${ticket.id}`).value;
                            if (resolution.trim()) {
                              handleUpdateStatus(ticket.id, 'resolved', ticket.assigned_to, resolution);
                            }
                          }}
                          disabled={updatingStatus}
                        >
                          {t('saveResolution')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TicketsListPage;
